import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import KPICard from '../components/dashboard/KPICard';
import DataTable from '../components/dashboard/DataTable';
import { 
  Lock, 
  Mail, 
  AlertTriangle, 
  Activity,
  Clock,
  Camera
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SharedView = () => {
  const { shareId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authRequired, setAuthRequired] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [liveData, setLiveData] = useState(null);
  
  // Auth form state
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  useEffect(() => {
    fetchShareAccess();
  }, [shareId]);

  const fetchShareAccess = async (authPassword = null, authEmail = null) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (authPassword) params.append('password', authPassword);
      if (authEmail) params.append('email', authEmail);
      
      const response = await axios.get(
        `${API_URL}/api/shares/${shareId}/access?${params.toString()}`
      );
      
      if (response.data.requires_auth) {
        setAuthRequired(response.data);
      } else {
        setShareData(response.data);
        setAuthRequired(null);
        
        // If live data, fetch current dashboard data
        if (response.data.data_type === 'live') {
          await fetchLiveData(response.data);
        }
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid password');
      } else if (err.response?.status === 403) {
        setError('Your email is not authorized to view this dashboard');
      } else if (err.response?.status === 410) {
        setError(err.response.data.detail || 'This link has expired or been revoked');
      } else {
        setError('Failed to load shared dashboard');
      }
    } finally {
      setLoading(false);
      setAuthLoading(false);
    }
  };

  const fetchLiveData = async (share) => {
    try {
      let endpoint = '';
      if (share.dashboard_type === 'scorecard') {
        endpoint = `/api/dashboard/executive/scorecard?year=${share.year}&period_type=${share.period_type}`;
        if (share.period_start) endpoint += `&period_start=${share.period_start}`;
        if (share.period_end) endpoint += `&period_end=${share.period_end}`;
      } else {
        endpoint = `/api/dashboard/${share.dashboard_type}?year=${share.year}&period_type=${share.period_type}`;
        if (share.period_start) endpoint += `&period_start=${share.period_start}`;
        if (share.period_end) endpoint += `&period_end=${share.period_end}`;
      }
      
      // For public access, we'll use the snapshot data if available
      // or show a message that authentication is required for live data
      const response = await axios.get(`${API_URL}${endpoint}`);
      setLiveData(response.data);
    } catch (err) {
      console.error('Failed to fetch live data:', err);
      // Live data requires auth, but we might have snapshot data
    }
  };

  const handleAuth = () => {
    setAuthLoading(true);
    if (authRequired?.auth_type === 'password') {
      fetchShareAccess(password, null);
    } else {
      fetchShareAccess(null, email);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] p-4 sm:p-8">
        <div className="max-w-6xl mx-auto">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-4 w-48 mb-8" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-40" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !authRequired) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-[#F59E0B] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#111827] mb-2">Unable to Access</h2>
            <p className="text-[#6B7280]">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Auth required state
  if (authRequired) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-12 h-12 bg-[#EFF6FF] rounded-full flex items-center justify-center mx-auto mb-3">
              {authRequired.auth_type === 'password' ? (
                <Lock className="w-6 h-6 text-[#2563EB]" />
              ) : (
                <Mail className="w-6 h-6 text-[#2563EB]" />
              )}
            </div>
            <CardTitle>{authRequired.title}</CardTitle>
            <p className="text-sm text-[#6B7280]">
              {authRequired.auth_type === 'password' 
                ? 'This dashboard is password protected'
                : 'Enter your email to access this dashboard'}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B] text-sm p-3 rounded-lg">
                {error}
              </div>
            )}
            
            {authRequired.auth_type === 'password' ? (
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  onKeyDown={(e) => e.key === 'Enter' && handleAuth()}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  onKeyDown={(e) => e.key === 'Enter' && handleAuth()}
                />
                {authRequired.allowed_domains?.length > 0 && (
                  <p className="text-xs text-[#6B7280]">
                    Allowed domains: {authRequired.allowed_domains.join(', ')}
                  </p>
                )}
              </div>
            )}
            
            <Button 
              onClick={handleAuth} 
              disabled={authLoading}
              className="w-full bg-[#2563EB] hover:bg-[#1D4ED8]"
            >
              {authLoading ? 'Verifying...' : 'Access Dashboard'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render shared dashboard
  const data = shareData?.data_type === 'snapshot' ? shareData?.data : liveData;
  
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7EB] px-4 sm:px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_metrics-hub-117/artifacts/yhkjnbgq_63ed5a01637c0a0b27bc74fc_Players-Health_logo-2.svg" 
                alt="Players Health" 
                className="h-7 w-auto"
              />
              <div className="w-px h-5 bg-[#E5E7EB]"></div>
              <Activity className="w-4 h-4 text-[#2563EB]" />
              <span className="text-[#111827] font-semibold text-sm">Pulse</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#6B7280]">
            {shareData?.data_type === 'snapshot' ? (
              <>
                <Camera className="w-4 h-4" />
                <span>Snapshot from {new Date(shareData.snapshot_date).toLocaleDateString()}</span>
              </>
            ) : (
              <>
                <Clock className="w-4 h-4" />
                <span>Live Data</span>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#111827]">{shareData?.title}</h1>
          <p className="text-[#6B7280]">
            {shareData?.year} • {shareData?.period_type?.charAt(0).toUpperCase() + shareData?.period_type?.slice(1)}
          </p>
        </div>

        {/* Dashboard content */}
        {shareData?.dashboard_type === 'scorecard' ? (
          // Executive Scorecard view
          <div className="space-y-8">
            {data?.scorecard && Object.entries(data.scorecard).map(([func, metrics]) => (
              <div key={func}>
                <h2 className="text-lg font-semibold text-[#111827] capitalize mb-4">{func}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {metrics.map((metric, idx) => (
                    <KPICard
                      key={idx}
                      title={metric.metric_name}
                      value={metric.actual_value}
                      unit={metric.unit}
                      planValue={metric.plan_value}
                      ragStatus={metric.plan_value ? 
                        (metric.actual_value >= metric.plan_value ? 'green' : 
                         metric.actual_value >= metric.plan_value * 0.95 ? 'yellow' : 'red') 
                        : 'no_plan'}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Single function dashboard view
          <div className="space-y-6">
            {data?.kpis ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.kpis.map((kpi, idx) => (
                  <KPICard
                    key={idx}
                    title={kpi.metric_name}
                    value={kpi.latest?.actual_value}
                    unit={kpi.latest?.unit}
                    planValue={kpi.latest?.plan_value}
                    ragStatus={kpi.rag_status}
                    variance={kpi.variance}
                    trend={kpi.trend}
                    yoy={kpi.yoy}
                  />
                ))}
              </div>
            ) : data?.kpi_values ? (
              // Snapshot data format
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(
                  data.kpi_values.reduce((acc, v) => {
                    if (!acc[v.metric_name] || v.period_start_date > acc[v.metric_name].period_start_date) {
                      acc[v.metric_name] = v;
                    }
                    return acc;
                  }, {})
                ).map(([name, metric]) => (
                  <KPICard
                    key={name}
                    title={name}
                    value={metric.actual_value}
                    unit={metric.unit}
                    planValue={metric.plan_value}
                    ragStatus={metric.plan_value ? 
                      (metric.actual_value >= metric.plan_value ? 'green' : 
                       metric.actual_value >= metric.plan_value * 0.95 ? 'yellow' : 'red') 
                      : 'no_plan'}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-[#6B7280]">
                <p>No data available for this view</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E5E7EB] mt-12 py-6 text-center text-sm text-[#6B7280]">
        <p>Shared via Players Health Pulse</p>
      </footer>
    </div>
  );
};

export default SharedView;
